import { Injectable } from '@angular/core';
import { BaseService, RequestOptions } from './base.service';
import { Observable, firstValueFrom } from 'rxjs';
import * as NodeForge from 'node-forge';

@Injectable({
	providedIn: 'root',
})
export class EncryptionService {

	private static readonly REQUEST_TOP_PATH: string = "login";

	public getPublicKey(requestOptions?: RequestOptions): Observable<string> {
		const requestEndpoint: string = "GetPublicKey";

		let url = `${EncryptionService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get<string>(
			url,
			requestOptions,
			undefined,
			false,
			'text'
		);
	}

	private publicKey: NodeForge.pki.rsa.PublicKey;

	private loadPublicKey(): void {
		if (!this.publicKey) {
			this.getPublicKey().subscribe(
				{
					next: (publicKey: string) => {
						this.publicKey = NodeForge.pki.publicKeyFromPem(publicKey) as NodeForge.pki.rsa.PublicKey;
					}
				}
			);
		}
	}

	async loadPublicKeyAsync() {
		if (!this.publicKey) {
			const publicKey = await firstValueFrom(this.getPublicKey());
			this.publicKey = NodeForge.pki.publicKeyFromPem(publicKey) as NodeForge.pki.rsa.PublicKey;
		}
	}

	public encrypt(data: string): string {
		if (!this.publicKey) {
			throw new Error('Public key not loaded.');
		}

		const encrypted: NodeForge.Base64 = this.publicKey.encrypt(
			data,
			'RSA-OAEP', // Using RSA with OAEP padding. Parameter specifies the padding scheme to use with RSA encryption.
			{
				md: NodeForge.md.sha256.create(), // Specifies the hash function used for the main hash in the OAEP padding scheme.
				mgf1: { // Mask Generation Function 1. Generates a mask of a specified length, which is used to mask parts of the plaintext during the OAEP padding process.
					md: NodeForge.md.sha256.create(), // Specifies the hash function used within MGF1.
				},
			}
		);

		return NodeForge.util.encode64(encrypted);
	}

	public encryptType(data: string): string {
		if (!this.publicKey) {
			throw new Error('Public key not loaded.');
		}

		const maxChunkSize = 190; // Adjusted maximum size for a 2048-bit RSA key with OAEP padding
		const chunks = this.chunkData(data, maxChunkSize);

		const encryptedChunks: string[] = [];

		for (const chunk of chunks) {
			try {
				const encrypted = this.publicKey.encrypt(chunk, 'RSA-OAEP', {
					md: NodeForge.md.sha256.create(),
					mgf1: {
						md: NodeForge.md.sha256.create(),
					},
				});
				encryptedChunks.push(NodeForge.util.encode64(encrypted));
			} catch (error) {
				console.error('Encryption error:', error);
				throw new Error('Encryption failed.');
			}
		}

		return encryptedChunks.join(''); // Combine all encrypted chunks
	}

	private chunkData(data: string, chunkSize: number): string[] {
		const chunks: string[] = [];
		for (let i = 0; i < data.length; i += chunkSize) {
			chunks.push(data.substring(i, i + chunkSize));
		}
		return chunks;
	}

	constructor(
		private readonly _baseService: BaseService,
	) {
		this.loadPublicKeyAsync().then(() => {
			console.log('Public key loaded');
		}).catch(error => {
			console.error('Error loading public key:', error);
		});
	}
}
